/**
 * Layout-Builder für den Planet-Hub: Mobile (Sidebar) und Desktop (vertikal).
 * Wird von PlanetMenuDOM.ts aufgerufen.
 */
import * as UI from './UITheme';
import type { TabId } from './PlanetMenuDOM';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'buildings', label: 'Geb\u00e4ude', icon: '\u2692' },
  { id: 'research', label: 'Forschung', icon: '\u269B' },
  { id: 'fleet', label: 'Flotte', icon: '\u{1F680}' },
];

export function mobileSidebarBtnStyle(active: boolean): string {
  const bg = active ? 'rgba(136,204,255,0.2)' : 'transparent';
  const col = active ? '#bdf' : '#4a6a7a';
  const border = active ? 'border-right:2px solid #6af;' : '';
  return `display:flex;align-items:center;justify-content:center;` +
    `width:100%;height:40px;background:${bg};color:${col};` +
    `border:none;${border}cursor:pointer;font-size:16px;` +
    `font-family:monospace;transition:background 0.15s,color 0.15s;`;
}

export function desktopTabStyle(active: boolean): string {
  const bg = active ? 'rgba(136,204,255,0.15)' : 'rgba(136,204,255,0.06)';
  const col = active ? '#bdf' : '#5a8aaa';
  const border = active ? 'rgba(136,204,255,0.25)' : 'rgba(136,204,255,0.1)';
  return `padding:${UI.padSm()} ${UI.padMd()};background:${bg};color:${col};` +
    `border:1px solid ${border};border-bottom:none;border-radius:8px 8px 0 0;` +
    `cursor:pointer;font-size:${UI.fontBase()};font-family:monospace;min-height:44px;` +
    (active ? '' : 'transition:background 0.15s,color 0.15s;');
}

export function buildMobileLayout(
  el: HTMLDivElement,
  titleEl: HTMLSpanElement,
  hubHeaderEl: HTMLDivElement,
  tabsEl: HTMLDivElement,
  contentEl: HTMLDivElement,
  onClose: () => void,
  onTabClick: (id: TabId) => void,
): HTMLDivElement {
  const sidebar = document.createElement('div');
  sidebar.style.cssText =
    'display:flex;flex-direction:column;align-items:center;width:44px;flex-shrink:0;' +
    'background:rgba(0,0,0,0.3);border-right:1px solid rgba(136,204,255,0.1);';

  const closeBtn = document.createElement('button');
  closeBtn.innerHTML = '\u2715';
  closeBtn.style.cssText =
    'width:44px;height:36px;background:none;border:none;border-bottom:1px solid rgba(136,204,255,0.1);' +
    'color:#8cf;cursor:pointer;font-size:14px;font-family:monospace;';
  closeBtn.addEventListener('click', onClose);
  sidebar.appendChild(closeBtn);

  tabsEl.style.cssText = 'display:flex;flex-direction:column;width:100%;';
  for (const t of TABS) {
    const btn = document.createElement('button');
    btn.innerHTML = t.icon;
    btn.title = t.label;
    btn.style.cssText = mobileSidebarBtnStyle(false);
    btn.dataset.tab = t.id;
    btn.addEventListener('click', () => onTabClick(t.id));
    tabsEl.appendChild(btn);
  }
  sidebar.appendChild(tabsEl);

  titleEl.style.cssText =
    'margin-top:auto;writing-mode:vertical-lr;text-orientation:mixed;' +
    'font-size:10px;letter-spacing:3px;color:#3a5a6a;padding:8px 0;' +
    'transform:rotate(180deg);';
  sidebar.appendChild(titleEl);
  el.appendChild(sidebar);

  const main = document.createElement('div');
  main.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;min-height:0;';
  hubHeaderEl.style.cssText += 'flex-shrink:0;';
  main.appendChild(hubHeaderEl);

  contentEl.style.cssText =
    'flex:1;display:flex;flex-direction:column;overflow-y:auto;' +
    'margin:4px 6px 6px;min-height:0;' +
    'background:rgba(136,204,255,0.03);border:1px solid rgba(136,204,255,0.12);' +
    'border-radius:6px;padding:6px;';
  main.appendChild(contentEl);

  const contentWrap = document.createElement('div');
  contentWrap.style.cssText =
    'display:flex;flex:1;min-height:0;opacity:0;transition:opacity 0.2s ease-out;';
  contentWrap.appendChild(sidebar);
  contentWrap.appendChild(main);
  el.appendChild(contentWrap);
  return contentWrap;
}

export function buildDesktopLayout(
  el: HTMLDivElement,
  titleEl: HTMLSpanElement,
  hubHeaderEl: HTMLDivElement,
  tabsEl: HTMLDivElement,
  contentEl: HTMLDivElement,
  onClose: () => void,
  onTabClick: (id: TabId) => void,
): HTMLDivElement {
  const header = document.createElement('div');
  header.style.cssText = `display:flex;align-items:center;padding:${UI.padLg()} ${UI.padXl()} 0;flex-shrink:0`;

  titleEl.style.cssText = `font-size:${UI.fontXl()};letter-spacing:2px;color:#bdf;margin-right:auto`;

  const closeBtn = document.createElement('button');
  closeBtn.innerHTML = '\u2715';
  closeBtn.style.cssText =
    'background:none;border:1px solid rgba(136,204,255,0.2);color:#8cf;' +
    'width:44px;height:44px;border-radius:6px;cursor:pointer;font-size:16px;' +
    'transition:background 0.15s;';
  closeBtn.addEventListener('click', onClose);

  header.appendChild(titleEl);
  header.appendChild(closeBtn);

  tabsEl.style.cssText = `display:flex;gap:${UI.gapSm()};padding:${UI.padMd()} ${UI.padXl()} 0;flex-shrink:0`;
  for (const t of TABS) {
    const btn = document.createElement('button');
    btn.textContent = t.label;
    btn.style.cssText = desktopTabStyle(false);
    btn.dataset.tab = t.id;
    btn.addEventListener('click', () => onTabClick(t.id));
    tabsEl.appendChild(btn);
  }

  contentEl.style.cssText =
    `flex:1;display:flex;flex-direction:column;overflow-y:auto;` +
    `margin:0 ${UI.padXl()} ${UI.padXl()};min-height:0;` +
    `background:rgba(136,204,255,0.03);border:1px solid rgba(136,204,255,0.12);` +
    `border-radius:0 8px 8px 8px;padding:${UI.padLg()}`;

  const contentWrap = document.createElement('div');
  contentWrap.style.cssText =
    'display:flex;flex-direction:column;flex:1;min-height:0;opacity:0;transition:opacity 0.2s ease-out;';
  contentWrap.appendChild(header);
  contentWrap.appendChild(hubHeaderEl);
  contentWrap.appendChild(tabsEl);
  contentWrap.appendChild(contentEl);
  el.appendChild(contentWrap);
  return contentWrap;
}
